import React, { useState, useEffect, useCallback } from 'react';
import './DocumentChecklistGenerator.css';

/**
 * DocumentChecklistGenerator Component
 * 
 * A personalized document checklist generator for Ireland visa applicants.
 * This is DIFFERENT from InteractiveChecklist - this generates a DYNAMIC
 * checklist based on the user's visa type and personal circumstances.
 * 
 * Target audience: Indian visa applicants
 * 
 * Features:
 * - Two-step wizard (visa type → circumstances → checklist)
 * - Comprehensive document lists for each visa type
 * - Conditional documents based on circumstances
 * - Progress tracking with localStorage persistence
 * - Print/PDF download functionality
 * - Share checklist link
 * - Estimated preparation time
 * - Document upload tips for each item
 */

// Storage key for localStorage persistence
const STORAGE_KEY = 'ireland_visa_checklist_data';

// 2026 planning references; role and permit rules must be verified before applying.
const GENERAL_PERMIT_SALARY = '€36,605';
const CRITICAL_SKILLS_SALARY = '€40,904';

// Visa types with their labels and icons
const VISA_TYPES = [
  { value: 'student', label: 'Student Visa (Stamp 2)', icon: '📚', description: 'For full-time degree programs at Irish institutions' },
  { value: 'work-permit', label: 'Work Permit (General Employment)', icon: '💼', description: 'For employment with an Irish employer' },
  { value: 'critical-skills', label: 'Critical Skills Employment Permit', icon: '⭐', description: 'For highly skilled occupations in demand' },
  { value: 'tourist', label: 'Tourist Visa', icon: '✈️', description: 'For short visits up to 90 days' },
  { value: 'dependent', label: 'Dependent/Spouse Visa', icon: '👨‍👩‍👧‍👦', description: 'For family members of visa holders' },
  { value: 'stamp-1g', label: 'Stamp 1G (Graduate)', icon: '🎓', description: 'For recent graduates seeking employment' }
];

// Personal circumstances questions
const CIRCUMSTANCE_QUESTIONS = [
  {
    id: 'applyingWith',
    question: 'Are you applying alone or with family?',
    icon: '👨‍👩‍👧‍👦',
    options: [
      { value: 'alone', label: 'Applying alone' },
      { value: 'with-spouse', label: 'With spouse/partner' },
      { value: 'with-children', label: 'With children' },
      { value: 'with-family', label: 'With spouse and children' }
    ]
  },
  {
    id: 'previousIrelandVisit',
    question: 'Have you been to Ireland before?',
    icon: '🛂',
    options: [
      { value: 'never', label: 'Never been to Ireland' },
      { value: 'previous-visa', label: 'Yes, on a previous visa' },
      { value: 'visa-refusal', label: 'Had a visa refusal before' }
    ]
  },
  {
    id: 'previousRefusals',
    question: 'Do you have any previous visa refusals (any country)?',
    icon: '⚠️',
    options: [
      { value: 'none', label: 'No refusals' },
      { value: 'ireland-refusal', label: 'Refused by Ireland' },
      { value: 'other-country', label: 'Refused by another country' },
      { value: 'multiple', label: 'Multiple refusals' }
    ]
  },
  {
    id: 'educationLocation',
    question: 'Is your highest education from India?',
    icon: '🎓',
    options: [
      { value: 'india', label: 'Yes, from India' },
      { value: 'abroad', label: 'From another country' },
      { value: 'both', label: 'Education from both India and abroad' }
    ]
  }
];

// Comprehensive document database organized by visa type
const DOCUMENT_DATABASE = {
  // Core documents required for ALL visa types
  core: [
    {
      id: 'passport',
      name: 'Valid Passport',
      category: 'Identity',
      required: true,
      description: 'Original passport with at least 6 months validity beyond your planned stay',
      tips: [
        'Ensure passport has at least 2 blank pages for visa stamp',
        'Make color copies of the first and last pages',
        'If you have old passports with previous visas, include those too'
      ],
      preparationTime: 0,
      uploadTips: 'Scan all pages clearly. File size should be under 5MB per page.'
    },
    {
      id: 'passport-photos',
      name: 'Passport-sized Photographs',
      category: 'Identity',
      required: true,
      description: 'Recent color photographs meeting Irish visa photo requirements',
      tips: [
        'Size: 35mm x 45mm with white background',
        'Taken within the last 6 months',
        'Face should cover 70-80% of the photo',
        'No glasses, head coverings (unless religious), or heavy makeup'
      ],
      preparationTime: 1,
      uploadTips: 'Scan at 300 DPI minimum. Ensure no shadows on face.'
    },
    {
      id: 'application-form',
      name: 'Completed Visa Application Form',
      category: 'Identity',
      required: true,
      description: 'Online application form submitted through AVATS system',
      tips: [
        'Apply online at avats.inis.gov.ie',
        'Print the completed application summary',
        'Sign and date the form',
        'Ensure all information matches your supporting documents'
      ],
      preparationTime: 1,
      uploadTips: 'Save as PDF after online submission. Include all pages.'
    },
    {
      id: 'application-fee',
      name: 'Visa Application Fee Receipt',
      category: 'Financial',
      required: true,
      description: 'Proof of visa fee payment',
      tips: [
        'Pay the fee at VFS Global after online application',
        'Keep the original receipt',
        'Fees are non-refundable even if visa is refused'
      ],
      preparationTime: 1,
      uploadTips: 'Scan the original receipt clearly showing payment details.'
    },
    {
      id: 'travel-insurance',
      name: 'Travel/Medical Insurance',
      category: 'Financial',
      required: true,
      description: 'Comprehensive travel insurance covering medical emergencies',
      tips: [
        'Minimum coverage of €30,000 for medical expenses',
        'Must cover the entire duration of stay',
        'Should include medical evacuation and repatriation',
        'Many Indian insurance companies offer Ireland-compliant policies'
      ],
      preparationTime: 3,
      uploadTips: 'Upload the complete policy document with coverage details highlighted.'
    }
  ],

  // Student Visa (Stamp 2) specific documents
  student: [
    {
      id: 'offer-letter',
      name: 'University/College Offer Letter',
      category: 'Education',
      required: true,
      description: 'Unconditional offer letter from an Irish educational institution',
      tips: [
        'Must be on official letterhead',
        'Should state course name, duration, and fees',
        'Unconditional offer is preferred; conditional offers may delay processing',
        'Keep original and make copies'
      ],
      preparationTime: 30,
      uploadTips: 'Scan the complete letter including all pages and conditions.'
    },
    {
      id: 'academic-transcripts',
      name: 'Academic Transcripts and Certificates',
      category: 'Education',
      required: true,
      description: 'All educational certificates from 10th standard onwards',
      tips: [
        'Include 10th, 12th, and degree certificates',
        'Get transcripts attested if required',
        'Include mark sheets for all semesters/years',
        'If applying for postgraduate, include undergraduate degree certificate'
      ],
      preparationTime: 7,
      uploadTips: 'Scan each certificate separately and also as a combined PDF.'
    },
    {
      id: 'english-test',
      name: 'English Language Test Results',
      category: 'Education',
      required: true,
      description: 'IELTS, TOEFL, or PTE Academic test results',
      tips: [
        'IELTS Academic minimum 6.0 overall (requirements vary by institution)',
        'Test should be taken within last 2 years',
        'Some institutions accept TOEFL iBT or PTE Academic',
        'Check specific requirements with your institution'
      ],
      preparationTime: 14,
      uploadTips: 'Include the original test report form scan with TRF number visible.'
    },
    {
      id: 'financial-proof',
      name: 'Proof of Sufficient Funds',
      category: 'Financial',
      required: true,
      description: 'Bank statements showing sufficient funds for tuition and living',
      tips: [
        'Show at least €10,000 per year for living expenses + tuition fees',
        'Bank statements for last 6 months',
        'Include fixed deposits, savings, and education loan sanction letters',
        'If sponsored, include sponsor\'s bank statements and sponsorship letter'
      ],
      preparationTime: 7,
      uploadTips: 'Ensure bank name, account holder name, and balances are clearly visible.'
    },
    {
      id: 'education-loan',
      name: 'Education Loan Sanction Letter (if applicable)',
      category: 'Financial',
      required: false,
      description: 'Bank loan approval letter for education funding',
      tips: [
        'Include complete loan sanction letter with terms',
        'Show disbursement schedule if available',
        'Loans from nationalized banks are preferred',
        'Include guarantor documents if applicable'
      ],
      preparationTime: 14,
      uploadTips: 'Scan all pages of the sanction letter including terms and conditions.'
    },
    {
      id: 'sponsor-letter',
      name: 'Sponsorship Letter (if sponsored)',
      category: 'Financial',
      required: false,
      description: 'Notarized sponsorship letter from parent/guardian',
      tips: [
        'Include sponsor\'s relationship to you',
        'State the amount being sponsored',
        'Include sponsor\'s income proof and bank statements',
        'Get the letter notarized'
      ],
      preparationTime: 3,
      uploadTips: 'Upload notarized copy with sponsor\'s ID proof.'
    },
    {
      id: 'accommodation-proof',
      name: 'Accommodation Proof',
      category: 'Logistics',
      required: false,
      description: 'Proof of arranged accommodation in Ireland',
      tips: [
        'University accommodation confirmation letter',
        'Or private rental agreement',
        'At least initial 7-14 days hotel booking is acceptable',
        'Include address and contact details'
      ],
      preparationTime: 7,
      uploadTips: 'Include booking confirmation or lease agreement.'
    },
    {
      id: 'purpose-statement',
      name: 'Statement of Purpose',
      category: 'Education',
      required: true,
      description: 'Personal statement explaining your study plans',
      tips: [
        'Explain why you chose this course and institution',
        'Describe your career goals and how this course helps',
        'Explain ties to India and reason to return',
        'Keep it 1-2 pages, well-structured'
      ],
      preparationTime: 3,
      uploadTips: 'Save as PDF with your signature at the end.'
    }
  ],

  // Work Permit (General Employment) specific documents
  'work-permit': [
    {
      id: 'job-offer',
      name: 'Job Offer Letter',
      category: 'Employment',
      required: true,
      description: 'Formal job offer from Irish employer',
      tips: [
        'Must be on company letterhead',
        'Include job title, salary, and start date',
        `Salary must meet the current role-specific threshold (2026 planning reference: ${GENERAL_PERMIT_SALARY})`,
        'Include details of the recruitment process'
      ],
      preparationTime: 14,
      uploadTips: 'Scan complete offer letter with all terms and conditions.'
    },
    {
      id: 'employment-contract',
      name: 'Employment Contract',
      category: 'Employment',
      required: true,
      description: 'Signed employment contract',
      tips: [
        'Must be for at least 2 years duration',
        'Include all terms and conditions',
        'Should specify 39+ hours per week',
        'Both employer and employee signatures required'
      ],
      preparationTime: 7,
      uploadTips: 'Include all pages of the signed contract.'
    },
    {
      id: 'employer-registration',
      name: 'Employer Registration with DETE',
      category: 'Employment',
      required: true,
      description: 'Proof that employer is registered with Department of Enterprise, Trade and Employment',
      tips: [
        'Employer must be registered with the Employment Permits Section',
        'Include employer\'s registration number',
        'Employer should provide this document',
        'Verify employer\'s legitimacy on Irish companies register'
      ],
      preparationTime: 7,
      uploadTips: 'Include official registration certificate or letter.'
    },
    {
      id: 'qualification-certificates',
      name: 'Educational Qualifications',
      category: 'Education',
      required: true,
      description: 'Degree certificates and transcripts relevant to the job',
      tips: [
        'Include all relevant degrees and diplomas',
        'May need to be recognized by Quality and Qualifications Ireland (QQI)',
        'Include professional certifications if relevant',
        'Get documents apostilled if required'
      ],
      preparationTime: 14,
      uploadTips: 'Scan each certificate separately with clear text.'
    },
    {
      id: 'experience-letters',
      name: 'Work Experience Letters',
      category: 'Employment',
      required: true,
      description: 'Reference letters from previous employers',
      tips: [
        'Include letters from all relevant past employers',
        'Should state job title, duration, and responsibilities',
        'Must be on company letterhead with authorized signature',
        'Include contact details for verification'
      ],
      preparationTime: 14,
      uploadTips: 'Scan original letters with company stamp and signature.'
    },
    {
      id: 'skills-proof',
      name: 'Proof of Skills/Qualifications',
      category: 'Employment',
      required: true,
      description: 'Evidence that you have the required skills for the role',
      tips: [
        'Include relevant certifications',
        'Training certificates',
        'Project portfolios',
        'LinkedIn recommendations can supplement'
      ],
      preparationTime: 7,
      uploadTips: 'Combine all relevant certificates into one PDF.'
    },
    {
      id: 'salary-proof',
      name: 'Salary Evidence',
      category: 'Financial',
      required: true,
      description: 'Evidence that salary meets minimum requirements',
      tips: [
        'Include salary mentioned in contract',
        'Compare to market rate for the role',
        'Include any allowances separately',
        `Salary must meet the current role-specific threshold (2026 planning reference: ${GENERAL_PERMIT_SALARY})`
      ],
      preparationTime: 0,
      uploadTips: 'Highlight salary details in the contract scan.'
    }
  ],

  // Critical Skills Employment Permit specific documents
  'critical-skills': [
    {
      id: 'cs-job-offer',
      name: 'Job Offer for Critical Skills Role',
      category: 'Employment',
      required: true,
      description: 'Job offer for a role on the Critical Skills Occupations List',
      tips: [
        'Role must be on the Critical Skills list',
        `Salary must meet the current Critical Skills threshold for the role (2026 planning reference: ${CRITICAL_SKILLS_SALARY})`,
        '2-year contract minimum',
        'Employer does not need to do Labour Market Needs Test'
      ],
      preparationTime: 14,
      uploadTips: 'Include complete offer letter with role details matching critical skills list.'
    },
    {
      id: 'cs-qualifications',
      name: 'Relevant Qualifications',
      category: 'Education',
      required: true,
      description: 'Degree in a relevant field for the critical skills role',
      tips: [
        'Degree must be relevant to the occupation',
        'May need QQI recognition',
        'Include transcripts and degree certificate',
        'Professional certifications add value'
      ],
      preparationTime: 14,
      uploadTips: 'Scan all relevant educational documents.'
    },
    {
      id: 'cs-experience',
      name: 'Relevant Work Experience',
      category: 'Employment',
      required: false,
      description: 'Experience in the critical skills field',
      tips: [
        'Not always required but strengthens application',
        'Include reference letters',
        'Show progression in career',
        'Highlight relevant projects and achievements'
      ],
      preparationTime: 14,
      uploadTips: 'Combine experience letters into one document.'
    },
    {
      id: 'cs-contract',
      name: 'Employment Contract',
      category: 'Employment',
      required: true,
      description: 'Signed employment contract for minimum 2 years',
      tips: [
        'Must be for at least 2 years',
        'Include all terms and conditions',
        'Salary must meet critical skills threshold',
        'Full-time position (39+ hours/week)'
      ],
      preparationTime: 7,
      uploadTips: 'Scan complete signed contract.'
    },
    {
      id: 'cs-employer-details',
      name: 'Employer Details and Registration',
      category: 'Employment',
      required: true,
      description: 'Information about the Irish employer',
      tips: [
        'Employer must be trading in Ireland',
        'At least 50% of employees must be EEA nationals (in most cases)',
        'Include employer registration details',
        'Company financial statements may be requested'
      ],
      preparationTime: 7,
      uploadTips: 'Include company registration certificate and any employer-provided documents.'
    }
  ],

  // Tourist Visa specific documents
  tourist: [
    {
      id: 'travel-itinerary',
      name: 'Travel Itinerary',
      category: 'Logistics',
      required: true,
      description: 'Detailed travel plans including dates and places to visit',
      tips: [
        'Include entry and exit dates',
        'List places you plan to visit',
        'Include internal travel arrangements if any',
        'Keep it realistic and achievable'
      ],
      preparationTime: 3,
      uploadTips: 'Create a day-by-day itinerary with dates and locations.'
    },
    {
      id: 'flight-bookings',
      name: 'Flight Reservations',
      category: 'Logistics',
      required: true,
      description: 'Return flight bookings (not necessarily paid)',
      tips: [
        'Show entry and exit from Ireland',
        'Reservation is sufficient, payment not required',
        'Include booking reference numbers',
        'Dates must match itinerary'
      ],
      preparationTime: 3,
      uploadTips: 'Include complete booking confirmation with dates and names.'
    },
    {
      id: 'hotel-bookings',
      name: 'Hotel/Accommodation Bookings',
      category: 'Logistics',
      required: true,
      description: 'Hotel reservations for the duration of stay',
      tips: [
        'Book hotels with free cancellation',
        'Include booking confirmations',
        'Address and contact details should be visible',
        'Duration must match travel dates'
      ],
      preparationTime: 3,
      uploadTips: 'Include all booking confirmations with dates and guest names.'
    },
    {
      id: 'tourist-funds',
      name: 'Proof of Sufficient Funds',
      category: 'Financial',
      required: true,
      description: 'Bank statements showing enough money for the trip',
      tips: [
        'Show at least €100/day for duration of stay',
        '6 months bank statements',
        'Include salary slips if employed',
        'Show savings and fixed deposits'
      ],
      preparationTime: 7,
      uploadTips: 'Ensure all pages are clear with account holder name visible.'
    },
    {
      id: 'employment-proof',
      name: 'Employment/Business Proof',
      category: 'Employment',
      required: true,
      description: 'Proof of employment or business in India',
      tips: [
        'Employed: NOC from employer, salary slips, ID card',
        'Self-employed: Business registration, GST returns, company bank statements',
        'Student: Bonafide certificate from institution',
        'Retired: Pension proof, retirement letter'
      ],
      preparationTime: 7,
      uploadTips: 'Include recent documents (within last 3 months).'
    },
    {
      id: 'invitation-letter',
      name: 'Invitation Letter (if visiting someone)',
      category: 'Logistics',
      required: false,
      description: 'Invitation from host in Ireland if staying with friends/family',
      tips: [
        'Host must sign the letter',
        'Include host\'s address and contact details',
        'State relationship and purpose of visit',
        'Host may need to provide passport/visa copy'
      ],
      preparationTime: 7,
      uploadTips: 'Include signed letter with host\'s ID proof.'
    },
    {
      id: 'cover-letter',
      name: 'Cover Letter',
      category: 'Identity',
      required: true,
      description: 'Personal cover letter explaining purpose of visit',
      tips: [
        'Explain purpose of visit',
        'Include travel dates and itinerary summary',
        'Mention who is funding the trip',
        'Express intent to return to India'
      ],
      preparationTime: 1,
      uploadTips: 'Sign and date the letter. Keep it to 1 page.'
    }
  ],

  // Dependent/Spouse Visa specific documents
  dependent: [
    {
      id: 'marriage-certificate',
      name: 'Marriage Certificate',
      category: 'Identity',
      required: true,
      description: 'Official marriage certificate',
      tips: [
        'Must be the original or certified copy',
        'May need apostille from MEA',
        'If married recently, include wedding photos/invitation',
        'Translation required if not in English'
      ],
      preparationTime: 7,
      uploadTips: 'Scan the complete certificate with all details visible.'
    },
    {
      id: 'birth-certificates',
      name: 'Birth Certificates (for children)',
      category: 'Identity',
      required: false,
      description: 'Birth certificates for dependent children',
      tips: [
        'Include for all children applying',
        'May need apostille',
        'Translation if not in English',
        'Include adoption papers if applicable'
      ],
      preparationTime: 7,
      uploadTips: 'Scan complete certificate with all details.'
    },
    {
      id: 'sponsor-passport',
      name: 'Sponsor\'s Passport and Visa Copy',
      category: 'Identity',
      required: true,
      description: 'Copy of sponsor\'s valid passport and Irish visa/permit',
      tips: [
        'Include bio page of passport',
        'Include current Irish visa/permit',
        'GNIB card if resident in Ireland',
        'Ensure sponsor\'s permit is valid'
      ],
      preparationTime: 3,
      uploadTips: 'Clear scans of all relevant pages.'
    },
    {
      id: 'sponsor-employment',
      name: 'Sponsor\'s Employment Proof',
      category: 'Employment',
      required: true,
      description: 'Proof of sponsor\'s employment and income in Ireland',
      tips: [
        'Employment contract or letter from employer',
        'Recent payslips (3-6 months)',
        'Must show income sufficient to support dependents',
        'Bank statements showing salary credits'
      ],
      preparationTime: 7,
      uploadTips: 'Include all employment and income proof documents.'
    },
    {
      id: 'sponsor-accommodation',
      name: 'Sponsor\'s Accommodation Proof',
      category: 'Logistics',
      required: true,
      description: 'Proof of accommodation in Ireland for family',
      tips: [
        'Rental agreement or property ownership proof',
        'Must be suitable for family size',
        'Landlord NOC if renting',
        'Utility bills as address proof'
      ],
      preparationTime: 7,
      uploadTips: 'Include complete rental agreement or property documents.'
    },
    {
      id: 'relationship-proof',
      name: 'Proof of Relationship',
      category: 'Identity',
      required: true,
      description: 'Evidence of genuine relationship',
      tips: [
        'Wedding photographs',
        'Communication records',
        'Joint financial documents if any',
        'Family photographs over time'
      ],
      preparationTime: 7,
      uploadTips: 'Create a PDF with selected photos and documents.'
    },
    {
      id: 'sponsor-financial',
      name: 'Sponsor\'s Financial Documents',
      category: 'Financial',
      required: true,
      description: 'Proof of sponsor\'s ability to support family',
      tips: [
        'Irish bank statements (6 months)',
        'Minimum €18,000/year + €3,000 per dependent',
        'Include savings and investments',
        'Show regular income'
      ],
      preparationTime: 7,
      uploadTips: 'Include complete bank statements with all pages.'
    }
  ],

  // Stamp 1G (Graduate) specific documents
  'stamp-1g': [
    {
      id: 'degree-certificate',
      name: 'Irish Degree Certificate',
      category: 'Education',
      required: true,
      description: 'Degree certificate from an Irish institution',
      tips: [
        'Must be from a recognized Irish college/university',
        'Include final transcript',
        'Letter from institution confirming graduation',
        'Must have completed a full-time degree program'
      ],
      preparationTime: 14,
      uploadTips: 'Scan the original degree certificate and transcript.'
    },
    {
      id: 'graduation-letter',
      name: 'Graduation Letter from Institution',
      category: 'Education',
      required: true,
      description: 'Official letter confirming graduation',
      tips: [
        'From the registrar or international office',
        'Confirm dates of study',
        'Confirm degree awarded',
        'Include student number'
      ],
      preparationTime: 7,
      uploadTips: 'Scan the complete letter on institution letterhead.'
    },
    {
      id: 'previous-gnib',
      name: 'Previous GNIB/IRP Card',
      category: 'Identity',
      required: true,
      description: 'Current or previous Irish Residence Permit',
      tips: [
        'Show previous Stamp 2 or Stamp 1G',
        'Include both sides of the card',
        'Must have been legally resident',
        'Apply before current permission expires'
      ],
      preparationTime: 0,
      uploadTips: 'Scan both sides of the card clearly.'
    },
    {
      id: 'job-seeking-evidence',
      name: 'Job Seeking Evidence',
      category: 'Employment',
      required: false,
      description: 'Evidence of active job search',
      tips: [
        'Job applications sent',
        'Interview invitations',
        'LinkedIn profile printout',
        'Recruitment agency registrations'
      ],
      preparationTime: 7,
      uploadTips: 'Compile evidence of your job search activities.'
    },
    {
      id: 'bank-statements',
      name: 'Irish Bank Statements',
      category: 'Financial',
      required: true,
      description: 'Bank statements showing funds for living',
      tips: [
        'Show at least €3,000 in savings',
        'Irish bank account preferred',
        '3-6 months statements',
        'Show regular transactions'
      ],
      preparationTime: 7,
      uploadTips: 'Include complete statements with your name and address.'
    },
    {
      id: 'rent-agreement',
      name: 'Accommodation Proof',
      category: 'Logistics',
      required: true,
      description: 'Current rental agreement or accommodation proof',
      tips: [
        'Current rental agreement',
        'Utility bills in your name',
        'Letter from landlord if informal arrangement',
        'Must show Irish address'
      ],
      preparationTime: 0,
      uploadTips: 'Include rental agreement and recent utility bill.'
    }
  ]
};

// Conditional documents based on circumstances
const CONDITIONAL_DOCUMENTS = {
  'applyingWith': {
    'with-spouse': [
      {
        id: 'spouse-passport',
        name: 'Spouse\'s Passport Copy',
        category: 'Identity',
        required: true,
        description: 'Copy of spouse\'s valid passport',
        tips: ['Include bio page and any previous visa pages', 'Must be valid for duration of stay'],
        preparationTime: 1,
        uploadTips: 'Scan all relevant pages clearly.'
      },
      {
        id: 'marriage-proof',
        name: 'Marriage Certificate',
        category: 'Identity',
        required: true,
        description: 'Official marriage certificate',
        tips: ['Original or certified copy', 'May need apostille', 'Translation if not in English'],
        preparationTime: 7,
        uploadTips: 'Scan complete certificate.'
      }
    ],
    'with-children': [
      {
        id: 'children-passports',
        name: 'Children\'s Passports',
        category: 'Identity',
        required: true,
        description: 'Valid passports for all children',
        tips: ['Each child needs their own passport', 'At least 6 months validity'],
        preparationTime: 14,
        uploadTips: 'Scan bio pages of each passport.'
      },
      {
        id: 'birth-certs',
        name: 'Children\'s Birth Certificates',
        category: 'Identity',
        required: true,
        description: 'Birth certificates for all children',
        tips: ['Original or certified copy', 'May need apostille'],
        preparationTime: 7,
        uploadTips: 'Scan complete certificates.'
      },
      {
        id: 'noc-father',
        name: 'NOC from Father (if applying without father)',
        category: 'Identity',
        required: false,
        description: 'No Objection Certificate from father for children traveling',
        tips: ['Required if father is not traveling', 'Notarized letter', 'Include father\'s ID proof'],
        preparationTime: 3,
        uploadTips: 'Scan notarized NOC with ID proof.'
      }
    ],
    'with-family': [
      {
        id: 'family-passports',
        name: 'Family Members\' Passports',
        category: 'Identity',
        required: true,
        description: 'Valid passports for spouse and all children',
        tips: ['Each person needs their own passport', 'At least 6 months validity'],
        preparationTime: 14,
        uploadTips: 'Scan bio pages of all passports.'
      },
      {
        id: 'family-certs',
        name: 'Marriage and Birth Certificates',
        category: 'Identity',
        required: true,
        description: 'Marriage certificate and children\'s birth certificates',
        tips: ['Original or certified copies', 'May need apostille', 'Translations if not in English'],
        preparationTime: 7,
        uploadTips: 'Scan all certificates clearly.'
      }
    ]
  },
  'previousIrelandVisit': {
    'previous-visa': [
      {
        id: 'previous-irish-visa',
        name: 'Previous Irish Visa Copy',
        category: 'Identity',
        required: true,
        description: 'Copy of previous Irish visa and entry/exit stamps',
        tips: ['Show compliance with previous visa', 'Include entry and exit stamps', 'Shows travel history'],
        preparationTime: 1,
        uploadTips: 'Scan previous visa and passport pages with stamps.'
      }
    ],
    'visa-refusal': [
      {
        id: 'refusal-letter',
        name: 'Previous Refusal Letter',
        category: 'Identity',
        required: true,
        description: 'Copy of the refusal letter from previous application',
        tips: ['Include the complete refusal letter', 'Shows reasons for refusal'],
        preparationTime: 1,
        uploadTips: 'Scan the complete refusal letter.'
      },
      {
        id: 'explanation-letter',
        name: 'Explanation Letter',
        category: 'Identity',
        required: true,
        description: 'Letter addressing the previous refusal reasons',
        tips: ['Address each refusal reason', 'Explain changed circumstances', 'Provide supporting evidence'],
        preparationTime: 7,
        uploadTips: 'Upload signed letter with supporting documents.'
      }
    ]
  },
  'previousRefusals': {
    'ireland-refusal': [
      {
        id: 'ireland-refusal-letter',
        name: 'Ireland Visa Refusal Letter',
        category: 'Identity',
        required: true,
        description: 'Copy of refusal letter from Irish immigration',
        tips: ['Include complete refusal letter', 'Address all refusal reasons in new application'],
        preparationTime: 1,
        uploadTips: 'Scan complete refusal letter.'
      },
      {
        id: 'ireland-explanation',
        name: 'Explanation Letter for Refusal',
        category: 'Identity',
        required: true,
        description: 'Detailed letter addressing refusal reasons',
        tips: ['Address each point raised', 'Show changed circumstances', 'Be honest and thorough'],
        preparationTime: 7,
        uploadTips: 'Include signed letter with evidence.'
      }
    ],
    'other-country': [
      {
        id: 'other-refusal-letter',
        name: 'Other Country Visa Refusal Letter',
        category: 'Identity',
        required: true,
        description: 'Copy of refusal letter from other country',
        tips: ['Must declare all refusals', 'Even if from other countries', 'Transparency is important'],
        preparationTime: 1,
        uploadTips: 'Scan refusal letter with translation if needed.'
      }
    ],
    'multiple': [
      {
        id: 'all-refusals',
        name: 'All Refusal Letters',
        category: 'Identity',
        required: true,
        description: 'Copies of all refusal letters from any country',
        tips: ['Must declare ALL refusals', 'Hiding refusals can lead to ban', 'Be completely transparent'],
        preparationTime: 3,
        uploadTips: 'Compile all refusal letters in one PDF.'
      },
      {
        id: 'comprehensive-explanation',
        name: 'Comprehensive Explanation Letter',
        category: 'Identity',
        required: true,
        description: 'Detailed letter addressing all refusal reasons',
        tips: ['Address each refusal separately', 'Show how circumstances have changed', 'Consider professional advice'],
        preparationTime: 14,
        uploadTips: 'Include detailed letter with supporting evidence.'
      }
    ]
  },
  'educationLocation': {
    'abroad': [
      {
        id: 'foreign-degree',
        name: 'Foreign Degree Certificate and Transcripts',
        category: 'Education',
        required: true,
        description: 'Degree certificates from institutions outside India',
        tips: ['May need apostille from issuing country', 'Translations if not in English', 'Recognition letter may be required'],
        preparationTime: 14,
        uploadTips: 'Scan all foreign educational documents.'
      }
    ],
    'both': [
      {
        id: 'all-degrees',
        name: 'All Degree Certificates',
        category: 'Education',
        required: true,
        description: 'Certificates from both Indian and foreign institutions',
        tips: ['Include all degrees', 'May need apostilles for foreign degrees', 'Translations where needed'],
        preparationTime: 14,
        uploadTips: 'Compile all educational certificates.'
      }
    ]
  }
};

// Document categories with icons
const CATEGORIES = {
  'Identity': { icon: '🪪', color: '#10B981' },
  'Financial': { icon: '💰', color: '#F59E0B' },
  'Education': { icon: '🎓', color: '#3B82F6' },
  'Employment': { icon: '💼', color: '#8B5CF6' },
  'Logistics': { icon: '🏠', color: '#EC4899' }
};

const DocumentChecklistGenerator = ({ onUpgrade }) => {
  // Current step in the wizard (1: visa type, 2: circumstances, 3: checklist)
  const [step, setStep] = useState(1);
  
  // Selected visa type
  const [visaType, setVisaType] = useState('');
  
  // Circumstance answers
  const [circumstances, setCircumstances] = useState({});
  
  // Current circumstance question index
  const [circumstanceStep, setCircumstanceStep] = useState(0);
  
  // Generated checklist
  const [checklist, setChecklist] = useState([]);
  
  // Checked items (persisted to localStorage)
  const [checkedItems, setCheckedItems] = useState({});
  
  // Expanded item descriptions
  const [expandedItems, setExpandedItems] = useState({});
  
  // Share link copied state
  const [linkCopied, setLinkCopied] = useState(false);

  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.visaType) {
          setVisaType(parsed.visaType);
        }
        if (parsed.circumstances) {
          setCircumstances(parsed.circumstances);
        }
        if (parsed.checkedItems) {
          setCheckedItems(parsed.checkedItems);
        }
        if (parsed.step) {
          setStep(parsed.step);
        }
        if (parsed.checklist) {
          setChecklist(parsed.checklist);
        }
      } catch (e) {
        console.error('Error loading saved checklist data:', e);
      }
    }
  }, []);

  // Save data to localStorage whenever relevant state changes
  const saveToLocalStorage = useCallback((data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Error saving checklist data:', e);
    }
  }, []);

  // Handle visa type selection
  const handleVisaTypeSelect = (type) => {
    setVisaType(type);
    setCircumstanceStep(0);
    setCircumstances({});
    saveToLocalStorage({ visaType: type, circumstances: {}, checkedItems: {} });
  };

  // Handle circumstance answer
  const handleCircumstanceAnswer = (questionId, value) => {
    const newCircumstances = { ...circumstances, [questionId]: value };
    setCircumstances(newCircumstances);
    
    if (circumstanceStep < CIRCUMSTANCE_QUESTIONS.length - 1) {
      setCircumstanceStep(circumstanceStep + 1);
      saveToLocalStorage({ visaType, circumstances: newCircumstances, checkedItems });
    } else {
      // All questions answered, generate checklist
      generateChecklist(newCircumstances);
    }
  };

  // Generate personalized checklist
  const generateChecklist = (currentCircumstances) => {
    let documents = [];
    
    // Add core documents
    documents = [...DOCUMENT_DATABASE.core];
    
    // Add visa-specific documents
    if (DOCUMENT_DATABASE[visaType]) {
      documents = [...documents, ...DOCUMENT_DATABASE[visaType]];
    }
    
    // Add conditional documents based on circumstances
    Object.entries(currentCircumstances).forEach(([questionId, answer]) => {
      if (CONDITIONAL_DOCUMENTS[questionId] && CONDITIONAL_DOCUMENTS[questionId][answer]) {
        documents = [...documents, ...CONDITIONAL_DOCUMENTS[questionId][answer]];
      }
    });
    
    // Remove duplicates based on id
    const uniqueDocuments = documents.filter((doc, index, self) =>
      index === self.findIndex(d => d.id === doc.id)
    );
    
    // Add unique IDs incorporating visa type and circumstances
    const finalDocuments = uniqueDocuments.map(doc => ({
      ...doc,
      uniqueId: `${visaType}-${doc.id}`
    }));
    
    setChecklist(finalDocuments);
    setStep(3);
    saveToLocalStorage({
      visaType,
      circumstances: currentCircumstances,
      checkedItems: {},
      step: 3,
      checklist: finalDocuments
    });
  };

  // Handle checkbox toggle
  const handleCheckItem = (uniqueId) => {
    const newCheckedItems = {
      ...checkedItems,
      [uniqueId]: !checkedItems[uniqueId]
    };
    setCheckedItems(newCheckedItems);
    saveToLocalStorage({ visaType, circumstances, checkedItems: newCheckedItems, step: 3, checklist });
  };

  // Toggle item expansion
  const toggleExpand = (uniqueId) => {
    setExpandedItems(prev => ({
      ...prev,
      [uniqueId]: !prev[uniqueId]
    }));
  };

  // Calculate progress
  const completedCount = Object.values(checkedItems).filter(Boolean).length;
  const requiredItems = checklist.filter(doc => doc.required);
  const completedRequired = requiredItems.filter(doc => checkedItems[doc.uniqueId]).length;
  const progressPercentage = checklist.length > 0 ? (completedCount / checklist.length) * 100 : 0;
  const requiredProgressPercentage = requiredItems.length > 0 ? (completedRequired / requiredItems.length) * 100 : 0;

  // Calculate total preparation time
  const totalPrepTime = checklist.reduce((sum, doc) => sum + doc.preparationTime, 0);

  // Group documents by category
  const groupedDocuments = checklist.reduce((acc, doc) => {
    if (!acc[doc.category]) {
      acc[doc.category] = [];
    }
    acc[doc.category].push(doc);
    return acc;
  }, {});

  // Handle print/PDF
  const handlePrint = () => {
    window.print();
  };

  // Handle share
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/checklist?v=${visaType}&c=${encodeURIComponent(JSON.stringify(circumstances))}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Ireland Visa Document Checklist',
          text: `Check my personalized Ireland visa document checklist for ${VISA_TYPES.find(v => v.value === visaType)?.label}`,
          url: shareUrl
        });
      } catch (err) {
        // User cancelled or share failed
        copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // Reset and start over
  const handleReset = () => {
    setStep(1);
    setVisaType('');
    setCircumstances({});
    setCircumstanceStep(0);
    setChecklist([]);
    setCheckedItems({});
    setExpandedItems({});
    localStorage.removeItem(STORAGE_KEY);
  };

  // Go back to previous step
  const handleBack = () => {
    if (step === 3) {
      setStep(2);
      setCircumstanceStep(CIRCUMSTANCE_QUESTIONS.length - 1);
    } else if (step === 2 && circumstanceStep > 0) {
      setCircumstanceStep(circumstanceStep - 1);
    } else if (step === 2) {
      setStep(1);
    }
  };

  // Render Step 1: Visa Type Selection
  const renderVisaTypeSelection = () => (
    <div className="dcg-step-container">
      <div className="dcg-header">
        <h2>📋 Document Checklist Generator</h2>
        <p className="dcg-subtitle">
          Get a personalized document checklist based on your visa type and circumstances
        </p>
      </div>

      <div className="dcg-visa-grid">
        {VISA_TYPES.map((type) => (
          <button
            key={type.value}
            className={`dcg-visa-card ${visaType === type.value ? 'selected' : ''}`}
            onClick={() => handleVisaTypeSelect(type.value)}
          >
            <span className="dcg-visa-icon">{type.icon}</span>
            <span className="dcg-visa-label">{type.label}</span>
            <span className="dcg-visa-description">{type.description}</span>
          </button>
        ))}
      </div>

      {visaType && (
        <div className="dcg-continue-section">
          <button className="dcg-btn-primary" onClick={() => setStep(2)}>
            Continue to Personal Circumstances →
          </button>
        </div>
      )}
    </div>
  );

  // Render Step 2: Circumstances Questions
  const renderCircumstances = () => {
    const currentQuestion = CIRCUMSTANCE_QUESTIONS[circumstanceStep];
    
    return (
      <div className="dcg-step-container">
        <div className="dcg-header">
          <h2>📝 Personal Circumstances</h2>
          <p className="dcg-subtitle">
            Answer a few questions to personalize your checklist
          </p>
        </div>

        <div className="dcg-progress-indicator">
          <div className="dcg-progress-steps">
            {CIRCUMSTANCE_QUESTIONS.map((_, index) => (
              <div
                key={index}
                className={`dcg-progress-step ${circumstanceStep >= index ? 'active' : ''} ${circumstanceStep > index ? 'completed' : ''}`}
              >
                <div className="dcg-step-circle">
                  {circumstanceStep > index ? '✓' : index + 1}
                </div>
              </div>
            ))}
          </div>
          <div className="dcg-progress-bar-container">
            <div
              className="dcg-progress-bar"
              style={{ width: `${((circumstanceStep + 1) / CIRCUMSTANCE_QUESTIONS.length) * 100}%` }}
            ></div>
          </div>
          <p className="dcg-progress-text">Question {circumstanceStep + 1} of {CIRCUMSTANCE_QUESTIONS.length}</p>
        </div>

        <div className="dcg-question-section">
          <div className="dcg-question-header">
            <span className="dcg-question-icon">{currentQuestion.icon}</span>
            <h3 className="dcg-question-text">{currentQuestion.question}</h3>
          </div>
          <div className="dcg-options-grid">
            {currentQuestion.options.map((option) => (
              <button
                key={option.value}
                className={`dcg-option-button ${circumstances[currentQuestion.id] === option.value ? 'selected' : ''}`}
                onClick={() => handleCircumstanceAnswer(currentQuestion.id, option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <button className="dcg-back-button" onClick={handleBack}>
          ← Back
        </button>
      </div>
    );
  };

  // Render Step 3: Generated Checklist
  const renderChecklist = () => (
    <div className="dcg-step-container dcg-checklist-view">
      <div className="dcg-header dcg-checklist-header">
        <div className="dcg-header-left">
          <h2>✅ Your Personalized Document Checklist</h2>
          <p className="dcg-subtitle">
            For {VISA_TYPES.find(v => v.value === visaType)?.label}
          </p>
        </div>
        <div className="dcg-header-actions">
          <button className="dcg-action-btn" onClick={handlePrint} title="Print/PDF">
            🖨️ Print
          </button>
          <button className="dcg-action-btn" onClick={handleShare} title="Share">
            {linkCopied ? '✓ Copied!' : '📤 Share'}
          </button>
          <button className="dcg-action-btn dcg-reset-btn" onClick={handleReset} title="Start Over">
            🔄 Reset
          </button>
        </div>
      </div>

      {/* Progress Summary */}
      <div className="dcg-progress-summary">
        <div className="dcg-progress-card">
          <div className="dcg-progress-circle">
            <svg viewBox="0 0 36 36" className="dcg-circular-progress">
              <path
                className="dcg-circle-bg"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="dcg-circle-progress"
                strokeDasharray={`${progressPercentage}, 100`}
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <div className="dcg-progress-value">{Math.round(progressPercentage)}%</div>
          </div>
          <div className="dcg-progress-info">
            <div className="dcg-progress-stat">
              <span className="dcg-stat-value">{completedCount}</span>
              <span className="dcg-stat-label">of {checklist.length} documents ready</span>
            </div>
            <div className="dcg-progress-stat dcg-required-progress">
              <span className="dcg-stat-value">{completedRequired}/{requiredItems.length}</span>
              <span className="dcg-stat-label">required documents</span>
            </div>
          </div>
        </div>
        
        <div className="dcg-time-estimate">
          <span className="dcg-time-icon">⏱️</span>
          <div className="dcg-time-info">
            <span className="dcg-time-label">Estimated Preparation Time</span>
            <span className="dcg-time-value">
              {totalPrepTime === 0 ? 'Same day' : 
               totalPrepTime < 7 ? `${totalPrepTime} days` :
               totalPrepTime < 30 ? `${Math.ceil(totalPrepTime / 7)} weeks` :
               `${Math.ceil(totalPrepTime / 30)} months`}
            </span>
          </div>
        </div>
      </div>

      {/* Required Progress Bar */}
      <div className="dcg-required-progress-bar">
        <div className="dcg-progress-bar-header">
          <span>Required Documents Progress</span>
          <span>{Math.round(requiredProgressPercentage)}%</span>
        </div>
        <div className="dcg-progress-bar-wrapper">
          <div
            className="dcg-progress-bar-fill"
            style={{ width: `${requiredProgressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Document Categories */}
      <div className="dcg-document-categories">
        {Object.entries(groupedDocuments).map(([category, documents]) => (
          <div key={category} className="dcg-category-section">
            <div className="dcg-category-header">
              <span className="dcg-category-icon" style={{ color: CATEGORIES[category]?.color }}>
                {CATEGORIES[category]?.icon}
              </span>
              <h3 className="dcg-category-title">{category}</h3>
              <span className="dcg-category-count">
                {documents.filter(d => checkedItems[d.uniqueId]).length}/{documents.length}
              </span>
            </div>
            
            <div className="dcg-document-list">
              {documents.map((doc) => (
                <div
                  key={doc.uniqueId}
                  className={`dcg-document-item ${checkedItems[doc.uniqueId] ? 'completed' : ''}`}
                >
                  <div className="dcg-document-main">
                    <label className="dcg-document-label">
                      <input
                        type="checkbox"
                        checked={checkedItems[doc.uniqueId] || false}
                        onChange={() => handleCheckItem(doc.uniqueId)}
                        className="dcg-document-checkbox"
                      />
                      <div className="dcg-document-info">
                        <span className="dcg-document-name">
                          {doc.name}
                          {doc.required ? (
                            <span className="dcg-required-badge">Required</span>
                          ) : (
                            <span className="dcg-optional-badge">Optional</span>
                          )}
                        </span>
                        <span className="dcg-document-description">{doc.description}</span>
                      </div>
                    </label>
                    <button
                      className={`dcg-expand-btn ${expandedItems[doc.uniqueId] ? 'expanded' : ''}`}
                      onClick={() => toggleExpand(doc.uniqueId)}
                      aria-label="Toggle details"
                    >
                      ▼
                    </button>
                  </div>
                  
                  {expandedItems[doc.uniqueId] && (
                    <div className="dcg-document-details">
                      <div className="dcg-tips-section">
                        <h4>💡 Tips</h4>
                        <ul>
                          {doc.tips.map((tip, index) => (
                            <li key={index}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="dcg-upload-tips">
                        <h4>📎 Upload Tips</h4>
                        <p>{doc.uploadTips}</p>
                      </div>
                      {doc.preparationTime > 0 && (
                        <div className="dcg-prep-time">
                          <span>⏱️ Estimated time: </span>
                          <span>
                            {doc.preparationTime < 7 ? `${doc.preparationTime} day(s)` :
                             doc.preparationTime < 30 ? `${Math.ceil(doc.preparationTime / 7)} week(s)` :
                             `${Math.ceil(doc.preparationTime / 30)} month(s)`}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Premium CTA */}
      {onUpgrade && (
        <div className="dcg-premium-cta">
          <div className="dcg-cta-icon">⭐</div>
          <div className="dcg-cta-content">
            <h4>Want Expert Document Review?</h4>
            <p>
              Get instant email + web push alerts and unlock all 13 premium tools,
              including expert document guidance before submission.
            </p>
            <ul className="dcg-premium-features">
              <li>✅ Instant email + web push alerts</li>
              <li>✅ Full access to all 13 premium tools</li>
              <li>✅ Expert document review and personalized feedback</li>
              <li>✅ Priority support</li>
              <li>✅ Checklist updates as rules change</li>
            </ul>
          </div>
          <button className="dcg-btn-primary dcg-cta-button" onClick={onUpgrade}>
            Upgrade for ₹349 – Alerts + 13 Tools →
          </button>
          <p className="dcg-cta-footnote">
            🔒 Secure payment • Instant activation
          </p>
        </div>
      )}

      <button className="dcg-back-button" onClick={handleBack}>
        ← Back to Questions
      </button>
    </div>
  );

  // Main render
  return (
    <div className="dcg-container">
      {step === 1 && renderVisaTypeSelection()}
      {step === 2 && renderCircumstances()}
      {step === 3 && renderChecklist()}
    </div>
  );
};

export default DocumentChecklistGenerator;
